import firebase, { storage } from "../../config/firebase";
import { toast } from "react-toastify";
import { deleteItem, duplicateItem } from "./itemsActions";

const db = firebase.firestore().collection("categories");


export const fetchCategories = (restaurantID, menuID) => (dispatch) => {
  return new Promise(async (resolve, reject) => {
    try {
      const categoriesSnapshot = await db
        .where("restaurantID", "==", restaurantID)
        .where("menuID", "==", menuID)
        .orderBy("createdAt", "desc")
        .get();

        
      const enrichedCategories = await Promise.all(
        categoriesSnapshot.docs.map(async (doc) => {
          const category = { id: doc.id, ...doc.data() };

          const itemSnap = await firebase
            .firestore()
            .collection("items")
            .where("categoryId", "==", category.id)
            .where("isVisible", "==", true)
            .limit(1)
            .get();

          return {
            ...category,
            hasItems: !itemSnap.empty,
          };
        })
      );

      dispatch({
        type: "FETCH_CATEGORY_SUCCESS",
        payload: enrichedCategories,
      });

      resolve(enrichedCategories);
    } catch (error) {
      console.error(error.message);
      toast.error(error.message || "Unable to fetch categories");
      reject(error);
    }
  });
};


export const addCategory =
  (restaurantID, menuID, name, imageFile) => (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        const categoryRef = db.doc();
        const categoryId = categoryRef.id;

        const storageRef = storage.ref(
          `categories/${categoryId}/${imageFile.name}`
        );
        await storageRef.put(imageFile);
        const imageUrl = await storageRef.getDownloadURL();

        const categoryData = {
          name,
          restaurantID,
          menuID,
          isVisible: true,
          image: imageUrl,
          createdAt: firebase.firestore.Timestamp.now(),
          updatedAt: firebase.firestore.Timestamp.now(),
        };

        await categoryRef.set(categoryData);

        toast.success("Category Added Successfully");
        dispatch({
          type: "ADD_CATEGORY_SUCCESS",
          payload: { ...categoryData, id: categoryId },
        });
        resolve(categoryData);
      } catch (error) {
        console.error(error.message);
        toast.error(error.message || "Unable to add Category");
        reject(error);
      }
    });
  };

export const updateCategory =
  (categoryId, name, imageFile, previousImageUrl) => (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        const categoryRef = db.doc(categoryId);

        const categoryData = {
          name,
          updatedAt: firebase.firestore.Timestamp.now(),
        };

        if (imageFile && imageFile instanceof File) {
          if (previousImageUrl) {
            const previousImageRef = storage.refFromURL(previousImageUrl);
            await previousImageRef.delete();
          }

          const storageRef = storage.ref(
            `categories/${categoryId}/${imageFile.name}`
          );
          await storageRef.put(imageFile);
          const newImageUrl = await storageRef.getDownloadURL();

          categoryData.image = newImageUrl;
        } else if (imageFile && typeof imageFile === "string") {
          categoryData.image = imageFile;
        }

        await categoryRef.update(categoryData);

        toast.success("Category Updated Successfully");

        dispatch({
          type: "UPDATE_CATEGORY_SUCCESS",
          payload: { ...categoryData, id: categoryId },
        });

        resolve(categoryData);
      } catch (error) {
        console.error(error.message);
        toast.error(error.message || "Unable to update Category");
        reject(error);
      }
    });
  };

export const deleteCategory = (categoryId, imageUrl) => (dispatch) => {
  return new Promise(async (resolve, reject) => {
    try {
      const itemsSnapshot = await firebase
        .firestore()
        .collection("items")
        .where("categoryId", "==", categoryId)
        .get();
      const itemsData = itemsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      for (const item of itemsData) {
        try {
          await dispatch(deleteItem(item.id, item?.images));
        } catch (err) {
          console.error("Error while deleting item", err);
        }
      }

      const categoryRef = db.doc(categoryId);

      await categoryRef.delete();

      try {
        const storageRef = storage.refFromURL(imageUrl);
        await storageRef.delete();
      } catch (err) {
        console.error("Error occured while deleting image", err);
      }

      dispatch({
        type: "DELETE_CATEGORY_SUCCESS",
        payload: categoryId,
      });

      resolve(categoryId);
    } catch (error) {
      console.error(error.message);
      toast.error(error.message || "Unable to delete Category");
      reject(error);
    }
  });
};

export const toggleCategoryVisibility =
  (categoryId, isVisible) => (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        await db.doc(categoryId).update({ isVisible });

        toast.success("Category visibility updated successfully!");
        dispatch({
          type: "UPDATE_CATEGORY_SUCCESS",
          payload: { id: categoryId, isVisible },
        });

        resolve({ id: categoryId, isVisible });
      } catch (error) {
        console.error(error.message);
        toast.error(error.message || "Unable to update Category visibility");
        reject(error);
      }
    });
  };

export const duplicateCategory =
  ({ restaurantID, categoryData }) =>
  async (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        const newCategoryRef = db.doc();
        const newCategoryId = newCategoryRef.id;

        const originalImageUrl = categoryData.image;
        const imageName = originalImageUrl.split("%2F").pop().split("?")[0];
        const newCategoryImageRef = storage.ref(
          `categories/${newCategoryId}/${imageName}`
        );

        const response = await fetch(originalImageUrl);
        const blob = await response.blob();
        await newCategoryImageRef.put(blob);
        const newImageUrl = await newCategoryImageRef.getDownloadURL();

        const newCategoryData = {
          restaurantID,
          name: categoryData.name,
          isVisible: categoryData.isVisible,
          menuID: categoryData.menuID,
          image: newImageUrl,
          createdAt: firebase.firestore.Timestamp.now(),
          updatedAt: firebase.firestore.Timestamp.now(),
        };

        await newCategoryRef.set(newCategoryData);

        const itemsSnap = await firebase
          .firestore()
          .collection("items")
          .where("categoryId", "==", categoryData?.id)
          .get();
        const duplicatedItems = [];

        for (const itemDoc of itemsSnap.docs) {
          await dispatch(
            duplicateItem({
              categoryId: newCategoryId,
              itemData: itemDoc?.data(),
            })
          );
        }

        toast.success("Category duplicated successfully");

        dispatch({
          type: "ADD_CATEGORY_SUCCESS",
          payload: { ...newCategoryData, id: newCategoryId },
        });

        resolve({ newCategoryData, duplicatedItems });
      } catch (error) {
        console.error(error.message);
        toast.error(error.message || "Unable to duplicate category");
        reject(error);
      }
    });
  };
